import type { Socket } from 'socket.io-client';

export interface ISocketOptions {
  debug?: boolean;
}

export interface SocketService {
  init: (options: ISocketOptions, callback: () => void) => void;
  on: (event: string, listener: (...args: any[]) => void) => void;
  off: (event: string, listener?: (...args: any[]) => void) => void;
  emit: (event: string, ...args: any[]) => void;
  disconnect: () => void;
  connect: () => void;
  isDebugEnabled: () => boolean;
}

const PING_INTERVAL = 30000;

let socket: Socket | null = null;
let debug = false;
let isInitialized = false;
let pingTimerId: any = null;
let isLoading = false;

function initSocket(callback = () => {}) {
  if (socket === null) return;

  socket.off('connect');
  socket.on('connect', () => {
    debug && console.warn('WebSocket - connected');
    callback();
    schedulePing();
  });

  socket.off('connect_error');
  socket.on('connect_error', (error) => {
    debug && console.warn('WebSocket: connection failed, attempting to reconnect...');
    debug && console.warn(`WebSocket: connection error ${error}`);
  });

  socket.off('disconnect');
  socket.on('disconnect', (reason) => {
    debug && console.warn('WebSocket - disconnected');
    stopPing();
    if (reason === 'io server disconnect') {
      socket?.connect();
    } else {
      isInitialized = false;
    }
  });

  socket.off('reconnect');
  socket.on('reconnect', () => {
    schedulePing();
  });

  socket.off('reconnect_failed');
  socket.on('reconnect_failed', () => {
    console.error('WebSocket - reconnection failed');
  });

  socket.off('error');
  socket.on('error', (error) => {
    console.error(`WebSocket error: ${error}`);
  });

  socket.offAny();
  socket.onAny((eventName, ...args) => {
    debug && console.warn(`WebSocket received message: event - ${eventName}: ${args}`);
  });

  socket.connect();
}

function doPing() {
  if (socket === null) return;
  if (socket.connected) {
    debug && console.warn('WebSocket - ping sent');
    socket.emit('ping');
    schedulePing();
  } else {
    debug && console.warn('WebSocket - not connected, trying to reconnect');
    socket.connect();
  }
}

function schedulePing() {
  clearTimeout(pingTimerId);
  pingTimerId = setTimeout(doPing, PING_INTERVAL);
}

function stopPing() {
  debug && console.warn('WebSocket - ping stopped');
  clearInterval(pingTimerId);
  pingTimerId = null;
}

/**
 * Lazy load Socket.io client and initialize socket connection
 * Called only after user logs in to reduce initial bundle size
 */
export async function initSocketService(): Promise<SocketService> {
  // If already initialized, return existing service
  if (socket && isInitialized) {
    return createSocketService();
  }

  // Prevent multiple simultaneous loads
  if (isLoading) {
    await new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!isLoading) {
          clearInterval(checkInterval);
          resolve(true);
        }
      }, 100);
    });
    return createSocketService();
  }

  isLoading = true;

  try {
    // Lazy load socket.io-client only when needed (after login)
    const { io } = await import('socket.io-client');

    // Get socket URL - only call useRuntimeConfig on client after plugin initialization
    const socketUrl = (useRuntimeConfig().public.socketUrl as string) || 'http://localhost:8080';

    socket = io(socketUrl, {
      autoConnect: false,
      transports: ['websocket'],
    });

    isLoading = false;
    return createSocketService();
  } catch (error) {
    isLoading = false;
    console.error('Failed to load Socket.io client:', error);
    throw error;
  }
}

function createSocketService(): SocketService {
  return {
    init(options: ISocketOptions = {}, callback = () => {}) {
      if (isInitialized) {
        callback();
        return;
      }
      debug = !!options.debug;
      initSocket(() => {
        isInitialized = true;
        callback();
      });
    },

    on(event: string, listener: (...args: any[]) => void) {
      socket?.on(event, listener);
    },

    off(event: string, listener?: (...args: any[]) => void) {
      socket?.off(event, listener);
    },

    emit(event: string, ...args: any[]) {
      socket?.emit(event, ...args);
    },

    disconnect() {
      socket?.disconnect();
    },

    connect() {
      socket?.connect();
    },

    isDebugEnabled() {
      return debug;
    },
  };
}

export function isSocketServiceLoaded(): boolean {
  return socket !== null;
}
